<template>
  <div>
    <b-table v-if="!helpersLoading"
             :current-page="currentPage"
             :empty-html="constantStrings.emptyDataHtml"
             :fields="tableFields"
             :items="proposals"
             :per-page="perPage"
             :responsive="true"
             :sort-by="'document_specification.document_id'"
             :sort-desc='true'
             class="table-responsive"
             head-variant="light"
             hover
             striped
             thead-class="text-center">

      <template v-slot:top-row="{fields}">
        <proposal-filter :id="id"
                         v-model="proposals"
                         :fields="fields"
                         :not-include-key="['actions', 'ordinalNumber']"
                         selected-call="None"
                         @input="filteredProposals"
                         @component-created="emitSetupProposal(proposals)"/>
      </template>

      <template v-slot:cell(ordinalNumber)="row">
        <base-column-ordinal-number v-bind="{index: row.index, perPage: perPage, currentPage: currentPage}"/>
      </template>

      <template v-slot:cell(experiment_color.evaluated_color)="row">
        <strong :class="'text-' + getExperimentColor(row.item.experiment_color.evaluated_color)">
          {{ row.item.experiment_color.evaluated_color }}
        </strong>
      </template>

      <template v-slot:cell(actions)="row">
        <div @mousedown="setSelectedProposal(row.item)">
          <action-download-pdf :proposal="row.item"/>

          <base-material-design-icon-button :title="evaluationModalTitle(row.item)"
                                            icon="fact_check"
                                            @click="openEvaluationModal(row.item)"/>

          <base-icon-button-send v-if="isProposalGradeSubmittable(row.item)"
                                 :disabled="!isUserEditor"
                                 class="d-block ml-auto"
                                 title="Submit evaluation"
                                 @click="openConfirmationModal"/>
        </div>
      </template>
    </b-table>
    <b-table-footer :current-page="currentPage"
                    :data-length="proposals.length"
                    :per-page="perPage"
                    :value="proposalsLoading"
                    @change-page="changePage">
      <template v-slot>
        <base-per-page-input v-model="perPage"/>
      </template>
    </b-table-footer>
  </div>
</template>

<script>
  import ActionDownloadPdf from '@/components/actions/ActionDownloadPdf';
  import BaseColumnOrdinalNumber from '@/components/base-components/BaseColumnOrdinalNumber';
  import BaseIconButtonSend from '@/components/base-components/base-icons/BaseIconButtonSend';
  import BaseMaterialDesignIconButton from '@/components/base-components/base-icons/BaseMaterialDesignIconButton';
  import BasePerPageInput from '@/components/base-components/BasePerPageInput';
  import ProposalFilter from '@/components/ProposalFilter';
  import BTableFooter from '@/components/BTableFooter';
  import constantStrings from '@/json/constantStrings';
  import { infrastructureMixin } from '@/mixins/infrastructure';
  import trafficLightsOptions from '@/json/trafficLightsAcceptanceMarkSafety';

  export default {
    name: 'RapidAccessTechnicalEvaluationTable',
    components: {
      ActionDownloadPdf,
      BaseColumnOrdinalNumber,
      BaseIconButtonSend,
      BaseMaterialDesignIconButton,
      BasePerPageInput,
      ProposalFilter,
      BTableFooter,
    },
    mixins: [infrastructureMixin],
    props: {
      helpersLoading: {
        type: Boolean,
        default: true,
      },
      isUserEditor: {
        type: Boolean,
        default: false,
      },
      proposalsLoading: {
        type: Boolean,
        default: false,
      },
      tableFields: {
        type: Array,
        default: () => [],
      },
      value: {
        type: Array,
        required: true,
      },
    },
    data() {
      return {
        id: 1,
        currentPage: 1,
        perPage: 20,
        proposals: [],
        toggleRefreshTable: true,
        constantStrings,
        trafficLightsOptions,
      };
    },
    // temporary change - to delete (with mixin) when changing cryo RA inhouse_grade into technical_evaluation
    computed: {
      isSynchrotron() {
        return this.infrastructure === 'synchrotron';
      },
    },
    watch: {
      value: {
        handler() {
          this.proposals = this.value;
          this.emitSetupProposal(this.proposals);
        },
        immediate: true,
        deep: true,
      },
    },
    methods: {
      filteredProposals(proposals) {
        this.$emit('filtered-proposals', proposals);
      },
      changePage(val) {
        this.currentPage = val;
      },
      getExperimentColor(status) {
        let variant = 'secondary';
        this.trafficLightsOptions.forEach((option) => {
          if (option.value === status) {
            variant = option.disabledVariant;
          }
        });
        return variant;
      },
      isProposalEvaluationEditable(proposal) {
        return this.isProposalGradeEditable(proposal) && this.isUserEditor;
      },
      isProposalGradeEditable(proposal) {
        if (this.isProposalGradeEmpty(proposal)) {
          return true;
        }
        return this.isProposalGradeStateDraft(proposal);
      },
      isProposalGradeEmpty(proposal) {
        // temporary change - to delete when changing cryo RA inhouse_grade into technical_evaluation
        if (this.isSynchrotron) {
          return this.isEmpty(proposal.technical_evaluation);
        }
        return this.isEmpty(proposal.inhouse_grade);
      },
      isProposalGradeStateDraft(proposal) {
        // temporary change - to change when changing cryo RA inhouse_grade into technical_evaluation
        const gradeStateName = this.isSynchrotron
          ? proposal.technical_evaluation.states.name
          : proposal.inhouse_grade.states.name;
        return gradeStateName === 'draft';
      },
      isProposalGradeSubmittable(proposal) {
        if (this.isProposalGradeEmpty(proposal)) {
          return false;
        }
        return this.isProposalGradeStateDraft(proposal);
      },
      evaluationModalTitle(proposal) {
        if (this.isProposalEvaluationEditable(proposal)) {
          return 'Edit evaluation';
        }
        return 'View evaluation';
      },
      openConfirmationModal() {
        this.$emit('open-submit-confirmation-modal');
      },
      openEvaluationModal(proposal) {
        const isEvaluationEditable = this.isProposalEvaluationEditable(proposal);
        this.$emit('open-rapid-access-evaluation-technical-modal', isEvaluationEditable);
      },
      setSelectedProposal(proposal) {
        const proposalWithoutRowVariant = { ...proposal };
        // eslint-disable-next-line no-underscore-dangle
        delete proposalWithoutRowVariant._rowVariant;
        this.$emit('set-selected-proposal', proposalWithoutRowVariant);
      },
    },
  };
</script>
